<template>
  <div class="module-container">
    <h3>图片管理</h3>

    <!-- 消息提示 -->
    <div v-if="message" class="success-message">{{ message }}</div>
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <!-- 上传区域 -->
    <div class="upload-section">
      <h4>上传新图片</h4>
      <div class="form-group">
        <label for="image-file">选择图片文件</label>
        <input type="file" id="image-file" @change="handleFileChange" accept=".jpg,.jpeg,.png" :disabled="isLoading" />
      </div>

      <div class="form-group">
        <label for="filename">文件名 (小写字母和数字)</label>
        <div class="filename-hint">例如: logo1</div>
        <div class="filename-input-wrapper">
          <input type="text" id="filename" v-model="form.filename" placeholder="输入文件名" :disabled="isLoading" />
          <span v-if="originalExtension" class="extension-display">{{ originalExtension }}</span>
        </div>
      </div>

      <div class="button-group">
        <button @click="uploadImage" :disabled="isLoading || !form.image || !form.filename" class="primary-button">
          <span v-if="isLoading">上传中...</span>
          <span v-else>上传图片</span>
        </button>
      </div>
    </div>

    <!-- 图片列表区域 -->
    <div class="images-section">
      <div class="section-header">
        <h4>已上传图片</h4>
        <button @click="loadImages" class="refresh-button" :disabled="isLoading">
          <span v-if="isLoading">刷新中...</span>
          <span v-else>刷新列表</span>
        </button>
      </div>

      <!-- 加载指示器 -->
      <div v-if="isLoading" class="loading-indicator">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>

      <div v-else-if="images.length === 0" class="empty-state">
        <div class="empty-icon">🖼️</div>
        <p>暂无图片，请上传新图片</p>
      </div>

      <div v-else class="images-grid">
        <div v-for="image in images" :key="image" class="image-item">
          <div class="image-preview">
            <img :src="`${baseUrl}${image}`" :alt="image" />
          </div>
          <div class="image-info">
            <span class="image-name">{{ image }}</span>
            <div class="image-actions">
              <button @click="copyImageUrl(image)" class="action-button copy-button" title="复制图片链接">
                复制链接
              </button>
              <button @click="deleteImage(image)" class="action-button delete-button" title="删除图片">
                删除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, inject, onMounted } from 'vue'

// 获取 API 基础 URL
const apiBaseUrl = inject('apiBaseUrl')

// 状态管理
const isLoading = ref(false)
const message = ref('')
const errorMessage = ref('')
const images = ref([])
const baseUrl = `${apiBaseUrl}/logo/`

// 添加文件扩展名相关变量
const originalExtension = ref('')
const baseFilename = ref('')

const form = reactive({
  image: null,
  filename: ''
})

// 计算完整文件名（基本名称 + 扩展名）
const fullFilename = computed(() => {
  if (!form.filename) return ''
  if (originalExtension.value) {
    // 如果用户输入的文件名已经包含了扩展名，则移除它
    const baseName = form.filename.replace(/\.[^/.]+$/, "")
    return `${baseName}${originalExtension.value}`
  }
  return form.filename
})

// 处理文件选择
const handleFileChange = (event) => {
  const file = event.target.files[0]
  if (file && ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
    form.image = file

    // 提取文件扩展名
    const fileName = file.name.toLowerCase()
    const extMatch = fileName.match(/\.[^/.]+$/)
    originalExtension.value = extMatch ? extMatch[0] : ''

    // 提取基本文件名（不含扩展名）
    baseFilename.value = fileName.replace(/\.[^/.]+$/, "")

    // 设置默认文件名为基本文件名
    form.filename = baseFilename.value

    errorMessage.value = ''
  } else {
    form.image = null
    originalExtension.value = ''
    baseFilename.value = ''
    form.filename = ''
    errorMessage.value = '请选择有效的图片文件 (JPG 或 PNG)'
  }
}

// 加载图片列表
const loadImages = async () => {
  isLoading.value = true
  message.value = '加载图片...'
  errorMessage.value = ''

  try {
    const response = await fetch(`${apiBaseUrl}/API/getImages.php`)
    const data = await response.json()

    if (data.success) {
      images.value = data.images
      message.value = `已加载 ${data.images.length} 张图片`
      setTimeout(() => {
        message.value = ''
      }, 2000)
    } else {
      errorMessage.value = data.message || '加载图片失败'
    }
  } catch (error) {
    errorMessage.value = '加载图片过程中发生错误'
    console.error('加载错误:', error)
  } finally {
    isLoading.value = false
  }
}

// 上传图片
const uploadImage = async () => {
  if (!form.image) {
    errorMessage.value = '请选择图片'
    return
  }

  if (!form.filename) {
    errorMessage.value = '请输入文件名'
    return
  }

  // 验证文件名格式 - 只检查是否包含小写字母和数字
  if (!/^[a-z0-9]+$/.test(form.filename)) {
    errorMessage.value = '文件名格式无效，只允许小写字母和数字'
    return
  }

  isLoading.value = true
  message.value = '上传中...'
  errorMessage.value = ''

  const formData = new FormData()
  formData.append('image', form.image)
  formData.append('filename', fullFilename.value)

  try {
    const response = await fetch(`${apiBaseUrl}/API/uploadImage.php`, {
      method: 'POST',
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      message.value = '上传成功！'
      form.image = null
      form.filename = ''
      originalExtension.value = ''
      baseFilename.value = ''

      // 重置文件输入
      const fileInput = document.getElementById('image-file')
      if (fileInput) fileInput.value = ''

      // 重新加载图片列表
      loadImages()
    } else {
      errorMessage.value = data.message || '上传失败'
      message.value = ''
    }
  } catch (error) {
    errorMessage.value = '上传过程中发生错误'
    console.error('上传错误:', error)
  } finally {
    isLoading.value = false
  }
}

// 删除图片
const deleteImage = async (filename) => {
  if (!confirm(`确定要删除图片 ${filename} 吗？`)) {
    return
  }

  isLoading.value = true
  message.value = '删除中...'
  errorMessage.value = ''

  try {
    const response = await fetch(`${apiBaseUrl}/API/deleteImage.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ filename })
    })

    const data = await response.json()

    if (data.success) {
      message.value = '删除成功！'
      // 从列表中移除已删除的图片
      images.value = images.value.filter(img => img !== filename)
    } else {
      errorMessage.value = data.message || '删除失败'
      message.value = ''
    }
  } catch (error) {
    errorMessage.value = '删除过程中发生错误'
    console.error('删除错误:', error)
  } finally {
    isLoading.value = false
  }
}

// 复制图片URL
const copyImageUrl = (filename) => {
  const url = `${apiBaseUrl}/logo/${filename}`
  navigator.clipboard.writeText(url)
    .then(() => {
      message.value = 'URL已复制到剪贴板！'
      setTimeout(() => {
        message.value = ''
      }, 2000)
    })
    .catch(err => {
      errorMessage.value = '复制失败'
      console.error('复制错误:', err)
    })
}

// 组件挂载时加载图片列表
onMounted(() => {
  loadImages()
})

// 暴露方法给父组件
defineExpose({
  loadImages
})
</script>

<style scoped>
.module-container {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.upload-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 5px;
  border: 1px solid #eee;
}

.upload-section h4 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.filename-hint {
  font-size: 12px;
  color: #666;
  margin-bottom: 5px;
  font-style: italic;
}

.filename-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.filename-input-wrapper input {
  width: 100%;
  padding: 10px;
  padding-right: 60px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.extension-display {
  position: absolute;
  right: 10px;
  color: #666;
  font-size: 14px;
  background-color: #f0f0f0;
  padding: 0 8px;
  border-radius: 3px;
  height: 70%;
  display: flex;
  align-items: center;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.primary-button {
  padding: 10px 20px;
  background-color: #FFD700;
  color: #333;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.primary-button:hover {
  background-color: #f0c800;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.primary-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.refresh-button {
  padding: 8px 15px;
  background-color: #e3f2fd;
  color: #1976d2;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-button:hover {
  background-color: #bbdefb;
}

.refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.images-section {
  margin-top: 30px;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 20px;
}

.image-item {
  background-color: #fff;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid #eee;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.image-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border-color: #ddd;
}

.image-preview {
  height: 180px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
}

.image-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.image-item:hover .image-preview img {
  transform: scale(1.05);
}

.image-info {
  padding: 15px;
}

.image-name {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
  word-break: break-all;
  font-weight: 500;
}

.image-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.action-button {
  padding: 6px 10px;
  font-size: 12px;
  border-radius: 4px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: pointer;
}

.copy-button {
  background-color: #e3f2fd;
  color: #1976d2;
  border: none;
}

.copy-button:hover {
  background-color: #bbdefb;
}

.delete-button {
  background-color: #ffebee;
  color: #d32f2f;
  border: none;
}

.delete-button:hover {
  background-color: #ffcdd2;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
  color: #999;
  background-color: #f9f9f9;
  border-radius: 5px;
  border: 1px dashed #ddd;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 215, 0, 0.3);
  border-radius: 50%;
  border-top-color: #FFD700;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.success-message {
  background-color: rgba(0, 128, 0, 0.1);
  color: #2e7d32;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 15px;
  border-left: 4px solid #2e7d32;
}

.error-message {
  background-color: rgba(255, 0, 0, 0.1);
  color: #d32f2f;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 15px;
  border-left: 4px solid #d32f2f;
}

@media (max-width: 992px) {
  .images-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .images-grid {
    grid-template-columns: 1fr;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .refresh-button {
    width: 100%;
  }
}
</style> 